
import React from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Wifi, WifiOff } from "lucide-react";
import { requestNotificationPermission } from "@/services/notificationService";

interface NotificationSettingsProps {
  notificationsEnabled: boolean;
  setNotificationsEnabled: (enabled: boolean) => void;
  reminderSettings: {
    morningReminder: boolean;
    morningReminderTime: string;
    eveningReminder: boolean;
    eveningReminderTime: string;
    smartNotifications: boolean;
  };
  onReminderSettingChange: (setting: string, value: any) => void;
  isOffline: boolean;
}

export const NotificationSettings: React.FC<NotificationSettingsProps> = ({
  notificationsEnabled,
  setNotificationsEnabled,
  reminderSettings,
  onReminderSettingChange,
  isOffline
}) => {
  return (
    <div className="py-4 space-y-6">
      {/* Paramètres des notifications */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Notifications</h3>
        
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="notifications">Activer les notifications</Label>
            <p className="text-sm text-gray-500">Recevoir des rappels pour le pointage</p>
          </div>
          <Switch
            id="notifications"
            checked={notificationsEnabled}
            onCheckedChange={async (checked) => {
              if (checked) {
                const granted = await requestNotificationPermission();
                setNotificationsEnabled(granted);
              } else {
                setNotificationsEnabled(false);
              }
            }}
          />
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="smart-notifications">Notifications intelligentes</Label>
            <p className="text-sm text-gray-500">Analyse contextuelle et alertes personnalisées</p>
          </div>
          <Switch
            id="smart-notifications"
            checked={reminderSettings.smartNotifications}
            onCheckedChange={(checked) => onReminderSettingChange('smartNotifications', checked)}
            disabled={!notificationsEnabled}
          />
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="morning-reminder">Rappel du matin</Label>
            <Switch
              id="morning-reminder"
              checked={reminderSettings.morningReminder}
              onCheckedChange={(checked) => onReminderSettingChange('morningReminder', checked)}
              disabled={!notificationsEnabled}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Label htmlFor="morning-time" className="shrink-0">Heure :</Label>
            <Input
              id="morning-time"
              type="time"
              value={reminderSettings.morningReminderTime}
              onChange={(e) => onReminderSettingChange('morningReminderTime', e.target.value)}
              disabled={!notificationsEnabled || !reminderSettings.morningReminder}
              className="max-w-[120px]"
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="evening-reminder">Rappel du soir</Label>
            <Switch
              id="evening-reminder"
              checked={reminderSettings.eveningReminder}
              onCheckedChange={(checked) => onReminderSettingChange('eveningReminder', checked)}
              disabled={!notificationsEnabled}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Label htmlFor="evening-time" className="shrink-0">Heure :</Label>
            <Input
              id="evening-time"
              type="time"
              value={reminderSettings.eveningReminderTime}
              onChange={(e) => onReminderSettingChange('eveningReminderTime', e.target.value)}
              disabled={!notificationsEnabled || !reminderSettings.eveningReminder}
              className="max-w-[120px]"
            />
          </div>
        </div>
      </div>
      
      {/* Statut de connexion */}
      <div className="space-y-2">
        <h3 className="text-lg font-medium">Connexion</h3>
        
        <div className="flex items-center space-x-2 p-4 rounded-md bg-gray-50">
          {isOffline ? (
            <WifiOff className="h-5 w-5 text-amber-500" />
          ) : (
            <Wifi className="h-5 w-5 text-green-500" />
          )}
          <div>
            <p className="font-medium">
              {isOffline ? "Mode hors-ligne" : "Connecté à Supabase"}
            </p>
            <p className="text-sm text-gray-500">
              {isOffline 
                ? "Les pointages seront synchronisés dès le retour de la connexion." 
                : "Les pointages sont enregistrés en temps réel avec notifications intelligentes."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
